import { useState } from 'react';
import { Typography, Button, Row, Col, Select } from 'antd';
import { Navigate, useNavigate } from 'react-router-dom';
import { SearchOutlined, FilterOutlined, AppstoreOutlined, CloseOutlined } from '@ant-design/icons';
import { useProducts, useAuth, Product, calculatePriceWithTier, getTierDiscountPercent } from '@hooks';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

export const MerchPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const { products } = useProducts({
    page: currentPage,
    limit: pageSize,
    search: searchQuery || undefined,
    category: categoryFilter || undefined,
  });

  const { isAuthenticated, profile } = useAuth();

  // Shop is not customer-ready yet (dummy product data) - keep it admin-only until launched.
  if (!profile.isLoading && profile.data?.profile !== 'admin') {
    return <Navigate to="/" replace />;
  }

  // Get user tier (default to retail if not authenticated or no tier)
  const userTier = profile.data?.tier || 'retail';
  const tierDiscountPercent = getTierDiscountPercent(userTier);

  const getProductImage = (product: Product): string => {
    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'estabrookinc-production';
    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-west-2';
    
    if (product.images && product.images.length > 0) {
      const img = product.images[0];
      return img.startsWith('http') ? img : `https://${bucketName}.s3.${region}.amazonaws.com/${img}`;
    }
    
    if (product.imageUrl) {
      return product.imageUrl.startsWith('http') 
        ? product.imageUrl 
        : `https://${bucketName}.s3.${region}.amazonaws.com/${product.imageUrl}`;
    }
    
    return `https://placehold.co/400x500/1a1a1a/00ade6?text=${encodeURIComponent(product.name)}`;
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numPrice);
  };

  const getMinVariantPrice = (product: Product): number => {
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => Number(v.price) || 0).filter(p => p > 0);
      return prices.length > 0 ? Math.min(...prices) : 0;
    }
    // Fallback for legacy products without variants
    return product.price ? Number(product.price) : 0;
  };

  const getTotalStock = (product: Product): number => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.reduce((total, variant) => total + (variant.stockQuantity || 0), 0);
    }
    // Fallback for legacy products without variants
    return product.stockQuantity || 0;
  };

  const getDiscountedPrice = (price: number | string): number => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 0;
    if (!isAuthenticated) return numPrice;
    return calculatePriceWithTier(numPrice, userTier);
  };

  const handleViewProduct = (productId: string) => {
    navigate(`/merch/${productId}`);
  };

  // Extract unique categories from products
  const categories = products.data?.data
    ? [...new Set(products.data.data.map(p => p.category).filter(Boolean))]
    : [];

  return (
    <div 
      className="text-white min-h-screen"
      style={{
        backgroundImage: "url('/assets/images/background-darker.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Hero Section */}
      <section className="relative w-full h-[400px] overflow-hidden">
        <div className="container h-full flex flex-col justify-center items-center mx-auto px-4">
          <Title 
            level={1} 
            className="font-azonix !font-normal text-primary text-center tracking-wider mb-6"
          >
            MERCH
          </Title>
          
          <div className="max-w-2xl mx-auto">
            <Paragraph className="text-center text-lg text-light">
              Premium gear designed for champions. Rep Estabrook with 
              quality merchandise that matches your dedication to the sport.
            </Paragraph>
          </div>
        </div>
      </section>

      {/* Filters Section - Enhanced */}
      <section className="sticky top-20 z-30 w-full py-4 bg-zinc-900/95 backdrop-blur-md border-b border-zinc-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              {/* Custom Search Input */}
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <SearchOutlined style={{ color: '#00ade6' }} className="transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full sm:w-72 h-11 pl-11 pr-4 bg-zinc-800/80 border border-zinc-700 text-white placeholder-zinc-500 font-coda text-sm focus:outline-none focus:border-[#00ade6] focus:ring-1 focus:ring-[#00ade6]/50 transition-all duration-200"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setCurrentPage(1);
                    }}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-white transition-colors"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Category Select */}
              <Select
                placeholder={
                  <span className="flex items-center gap-2 text-white">
                    <FilterOutlined style={{ color: '#00ade6' }} /> Category
                  </span>
                }
                allowClear={!!categoryFilter}
                value={categoryFilter || undefined}
                onChange={(value) => {
                  setCategoryFilter(value || '');
                  setCurrentPage(1);
                }}
                className={`w-full sm:w-48 [&_.ant-select-selector]:!bg-zinc-800/80 [&_.ant-select-selector]:!border-zinc-700 [&_.ant-select-selector]:!h-11 [&_.ant-select-selection-placeholder]:!text-zinc-500 [&_.ant-select-selection-item]:!text-white [&_.ant-select-arrow]:!text-[#00ade6] [&_.ant-select-clear]:!bg-zinc-800 [&_.ant-select-clear]:!text-[#00ade6] [&_.ant-select-clear_svg]:!fill-[#00ade6] [&_.ant-select-clear]:!top-[60%] ${categoryFilter ? '[&_.ant-select-arrow]:!hidden' : '[&_.ant-select-arrow]:!top-[55%]'}`}
                size="large"
                popupClassName="!bg-zinc-800 !border-zinc-700 [&_.ant-select-item]:!text-zinc-300 [&_.ant-select-item-option-active]:!bg-zinc-700 [&_.ant-select-item-option-selected]:!bg-[#00ade6]/20 [&_.ant-select-item-option-selected]:!text-[#00ade6] [&_.ant-empty-description]:!text-white"
              >
                {categories.map((cat) => (
                  <Option key={cat} value={cat}>{cat}</Option>
                ))}
              </Select>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-zinc-800/50 px-4 py-2.5 border border-zinc-700/50">
                <AppstoreOutlined style={{ color: '#00ade6' }} />
                <Text className="font-coda text-white font-medium">
                  {products.data?.pagination?.totalCount || 0} <span className="text-zinc-400">{(products.data?.pagination?.totalCount || 0) === 1 ? 'product' : 'products'}</span>
                </Text>
              </div>
              
              {(searchQuery || categoryFilter) && (
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setCategoryFilter('');
                    setCurrentPage(1);
                  }}
                  icon={<CloseOutlined className="text-[#00ade6]" />}
                  className="bg-zinc-800/50 hover:bg-zinc-700/50 border-zinc-700/50 hover:border-[#00ade6]/50 text-white hover:text-[#00ade6] font-coda"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Products Section */}
      <section className="w-full py-12 bg-dark">
        <div className="container mx-auto px-4">
          {products.isLoading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-zinc-800 border-t-[#00ade6] rounded-full animate-spin" />
              </div>
              <Text className="font-coda text-white mt-6">Loading products...</Text>
            </div>
          ) : !products.data?.data || products.data.data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-24 h-24 rounded-full bg-zinc-800/50 flex items-center justify-center mb-6">
                <AppstoreOutlined className="text-4xl text-primary" />
              </div>
              <Text className="font-coda text-white text-lg mb-2">No products found</Text>
              <Text className="font-coda text-white text-sm">Try adjusting your search or filters</Text>
            </div>
          ) : (
            <>
              <Row gutter={[20, 24]}>
                {products.data.data.map((product: Product, index: number) => {
                  const minPrice = getMinVariantPrice(product);
                  const originalPrice = minPrice;
                  const discountedPrice = getDiscountedPrice(minPrice);
                  const hasDiscount = isAuthenticated && tierDiscountPercent > 0;
                  const hasVariants = product.variants && product.variants.length > 0;

                  return (
                    <Col xs={24} sm={12} md={8} lg={6} key={product.id} className="flex">
                      <div 
                        className="group relative bg-zinc-900/50 overflow-hidden border border-zinc-800/50 hover:border-[#00ade6]/50 transition-all duration-500 h-full w-full flex flex-col cursor-pointer hover:shadow-[0_0_20px_rgba(0,173,230,0.05)] hover:-translate-y-1"
                        onClick={() => handleViewProduct(product.id)}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        {/* Image Container */}
                        <div className="aspect-square overflow-hidden bg-zinc-900 relative">
                          <img 
                            src={getProductImage(product)} 
                            alt={product.name} 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                            onError={(e) => {
                              e.currentTarget.src = `https://placehold.co/400x500/1a1a1a/00ade6?text=${encodeURIComponent(product.name)}`;
                            }}
                          />
                          {/* Gradient overlay on hover */}
                          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          
                          {/* Category badge overlay */}
                          {product.category && (
                            <div className="absolute top-3 left-3">
                              <span className="font-coda text-xs uppercase tracking-wider bg-zinc-950/80 backdrop-blur-sm text-[#00ade6] px-3 py-1.5 border border-zinc-700/50">
                                {product.category}
                              </span>
                            </div>
                          )}
                          
                          {/* Stock badge */}
                          {isAuthenticated && (() => {
                            const totalStock = getTotalStock(product);
                            return totalStock <= 5 && totalStock > 0 && (
                              <div className="absolute top-3 right-3">
                                <span className="font-coda text-xs uppercase tracking-wider bg-amber-500/90 text-black px-3 py-1.5 font-bold">
                                  Only {totalStock} left
                                </span>
                              </div>
                            );
                          })()}
                          
                          {/* Out of stock overlay */}
                          {isAuthenticated && getTotalStock(product) === 0 && (
                            <div className="absolute inset-0 bg-zinc-950/70 flex items-center justify-center">
                              <span className="font-azonix text-lg uppercase tracking-wider text-white font-bold">
                                Sold Out
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="p-5 flex flex-col flex-1">
                          {/* Product Name */}
                          <h3 className="font-azonix text-white text-lg font-medium mb-3 group-hover:text-[#00ade6] transition-colors duration-300">
                            {product.name && product.name.length > 80 
                              ? `${product.name.substring(0, 80)}...` 
                              : product.name}
                          </h3>

                          {/* Product Description */}
                          {product.description && (
                            <Text className="font-coda text-white text-sm block mb-4">
                              {product.description.length > 100 
                                ? `${product.description.substring(0, 100)}...` 
                                : product.description}
                            </Text>
                          )}

                          {/* Price Display */}
                          {isAuthenticated && (
                            <div className="mb-4">
                              <div className="flex items-baseline gap-3 flex-wrap">
                                {hasVariants && (
                                  <span className="font-coda text-zinc-400 text-sm font-normal">
                                    starting at:
                                  </span>
                                )}
                                <span className="font-azonix text-white font-normal tracking-wider text-2xl">
                                  {formatPrice(discountedPrice)}
                                </span>
                                {hasDiscount && (
                                  <span className="font-coda text-zinc-500 line-through text-sm">
                                    {formatPrice(originalPrice)}
                                  </span>
                                )}
                              </div>
                              {hasDiscount && (
                                <div className="mt-2">
                                  <span className="font-coda text-xs uppercase tracking-wider bg-emerald-500/20 text-emerald-400 px-2 py-1">
                                    Save {tierDiscountPercent}%
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Spacer to push button to bottom */}
                          <div className="flex-1"></div>

                          {/* View Button - Fixed at bottom */}
                          <Button 
                            className="btn-double-cut !w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProduct(product.id);
                            }}
                          >
                            VIEW DETAILS
                          </Button>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>

              {/* Pagination - Enhanced */}
              {products.data.pagination && products.data.pagination.totalPages > 1 && (
                <div className="flex justify-center mt-16">
                  <div className="inline-flex items-center gap-2 bg-zinc-900/80 backdrop-blur-sm p-1.5 border border-zinc-800">
                    <Button
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(p => p - 1)}
                      className="bg-transparent hover:bg-zinc-800 text-white border-none h-10 px-6 disabled:opacity-30 disabled:hover:bg-transparent"
                    >
                      ← Prev
                    </Button>
                    <div className="flex items-center px-4">
                      <Text className="font-coda text-zinc-400">
                        <span className="text-white font-bold">{currentPage}</span>
                        <span className="mx-2">/</span>
                        <span>{products.data.pagination.totalPages}</span>
                      </Text>
                    </div>
                    <Button
                      disabled={currentPage >= products.data.pagination.totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}
                      className="bg-[#00ade6] hover:bg-[#00c4ff] text-black border-none h-10 px-6 font-bold disabled:opacity-30"
                    >
                      Next →
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      
      {/* CTA Section - Enhanced with gradient and pattern */}
      <section 
        className="relative w-full py-20 overflow-hidden"
        style={{
          backgroundImage: "url('/assets/images/background-darker.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <Title level={1} className="font-azonix text-primary mb-4 uppercase !font-normal">
            Bulk Orders Available
          </Title>
          <Paragraph className="font-coda text-white text-lg md:text-xl mb-10 max-w-2xl mx-auto tracking-wide">
            Outfit your entire team with custom Estabrook gear. Special pricing and customization options for orders of 10+ items.
          </Paragraph>
          <Button 
            onClick={() => navigate('/contact')}
            className="btn-double-cut !w-fit !bg-[#00ade6] hover:!bg-[#00c4ff] !text-white !border-[#00ade6] hover:!border-[#00c4ff]"
          >
            GET A QUOTE
          </Button>
        </div>
      </section>
    </div>
  );
};
